import { useEffect, useState } from "react";
import { initsPopupResolverProactively } from "../lib/popupResolverProactive";
import {
  isPopupResolverReady,
  schedulePopupResolverWarmUp,
  subscribePopupResolver,
  warmUpPopupResolverNow,
} from "../lib/popupResolverWarmUp";

/** How long a sign-in button waits for the popup sign-in iframe before it can be pressed anyway. */
export const POPUP_SIGN_IN_HOLD_MS = 8000;

/**
 * ENG-3625: for a surface that opens a sign-in popup (AuthScreen's provider buttons, Request to join's
 * "Find my organization"). Loads the popup sign-in iframe while the surface is mounted, and returns
 * whether its buttons can be pressed. `active` is false while the surface shows no such button.
 *
 * - **Mobile browsers, Safari and iOS** (lib/popupResolverProactive.ts) block a window opened after a
 *   network wait, and a popup call waits for the iframe if it has not loaded. Auth used to load it
 *   before the first auth state there, behind the full-page spinner. Now the page shows at once, the
 *   load starts as soon as the surface mounts, and only its buttons wait: false until the iframe has
 *   loaded, the load has failed, or POPUP_SIGN_IN_HOLD_MS have passed. After a failure or the timeout
 *   a press may be blocked, and the "press again" message (lib/authErrors.ts) covers that.
 * - **Everywhere else** the popup is not blocked after the wait, so the buttons are never held. The
 *   load is scheduled after paint, as an idle task, so a press usually finds it done.
 *
 * Signed-in pages without such a surface load nothing.
 */
export function usePopupSignInReady(active = true): boolean {
  const [proactive] = useState(() => initsPopupResolverProactively());
  const [held, setHeld] = useState(() => proactive && !isPopupResolverReady());

  // Ahead of the warm-up below, so a warm-up that fails at once is still heard.
  useEffect(() => {
    if (!active || !held) return undefined;
    const release = () => setHeld(false);
    const unsubscribe = subscribePopupResolver(release);
    if (!unsubscribe || isPopupResolverReady()) {
      unsubscribe?.();
      release();
      return undefined;
    }
    const timer = window.setTimeout(release, POPUP_SIGN_IN_HOLD_MS);
    return () => {
      unsubscribe();
      window.clearTimeout(timer);
    };
  }, [active, held]);

  useEffect(() => {
    if (!active) return undefined;
    if (!proactive) return schedulePopupResolverWarmUp();
    warmUpPopupResolverNow();
    return undefined;
  }, [active, proactive]);

  return !active || !held;
}
